import React from 'react';
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

export const Mitgliedschaften: React.FC = () => {
    const { t } = useTranslation();
    usePageMetadata('mitgliedschaften');

    const { data: affiliations, isLoading } = useQuery({
        queryKey: ['publicAffiliations'],
        queryFn: async () => {
            const response = await apiClient.get('/public/affiliations');
            return response.data.data;
        }
    });

    return (
        <div className="flex flex-col py-16 sm:py-24 bg-secondary/30 min-h-screen">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">

                <nav className="mb-8 text-sm font-medium text-foreground/60 flex items-center gap-2">
                    <Link to="/" className="hover:text-primary transition-colors">{t('common.home')}</Link>
                    <span>/</span>
                    <span className="text-foreground">{t('mitgliedschaften.breadcrumb')}</span>
                </nav>

                {/* Header */}
                <div className="max-w-2xl mb-16">
                    <motion.h1
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl"
                    >
                        {t('mitgliedschaften.title')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="mt-4 text-lg leading-8 text-foreground/70"
                    >
                        {t('mitgliedschaften.description')}
                    </motion.p>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {isLoading ? (
                        <div className="col-span-full flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : affiliations && affiliations.length > 0 ? (
                        affiliations.map((item: any, index: number) => {
                            const CardWrapper = item.linkUrl ? "a" : "div";
                            const props = item.linkUrl ? { href: item.linkUrl, target: "_blank", rel: "noopener noreferrer" } : {};

                            return (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`relative flex flex-col items-center justify-center p-8 bg-background border border-border shadow-sm rounded-2xl transition-all ${item.linkUrl ? 'hover:shadow-md hover:border-primary/50 cursor-pointer group' : ''}`}
                                    {...props as any}
                                    as={CardWrapper as any}
                                >
                                    <div className="h-24 w-full flex items-center justify-center">
                                        <img
                                            src={resolveAssetUrl(item.logoUrl)}
                                            alt={item.name || "Affiliation"}
                                            className={`max-h-full max-w-full object-contain ${item.linkUrl ? 'group-hover:scale-105 transition-transform' : ''}`}
                                        />
                                    </div>
                                    {item.name && (
                                        <div className="mt-6 text-center">
                                            <h3 className={`font-semibold text-sm ${item.linkUrl ? 'group-hover:text-primary transition-colors text-foreground' : 'text-foreground/80'}`}>
                                                {item.name}
                                            </h3>
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })
                    ) : (
                        <div className="col-span-full rounded-2xl border border-dashed border-border bg-background p-12 text-center">
                            <h3 className="text-lg font-medium text-foreground">{t('mitgliedschaften.empty')}</h3>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};
